import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'https://api.karrentals.ca/shuttle';

function useQuery() {
  const { search } = useLocation();
  return useMemo(
    () => Object.fromEntries(new URLSearchParams(search)),
    [search]
  );
}

/**
 * Parse a date-only or ISO string WITHOUT causing a timezone shift.
 * - "2025-12-13"     -> local Date(2025, 11, 13)
 * - "2025-12-13T..." -> local Date(2025, 11, 13) for date labels
 */
function formatLocalDateString(
  value,
  options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
) {
  if (!value) return '';

  const s = String(value);

  // Pure date: YYYY-MM-DD
  const m1 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m1) {
    const [_, y, m, d] = m1;
    const dt = new Date(Number(y), Number(m) - 1, Number(d)); // LOCAL
    return dt.toLocaleDateString(undefined, options);
  }

  // ISO-ish: YYYY-MM-DDTHH...
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (m2) {
    const [_, y, m, d] = m2;
    const dt = new Date(Number(y), Number(m) - 1, Number(d)); // LOCAL, ignore time
    return dt.toLocaleDateString(undefined, options);
  }

  // Fallback: let JS handle it (for weird strings)
  try {
    const dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return s;
    return dt.toLocaleDateString(undefined, options);
  } catch {
    return s;
  }
}

/**
 * Full datetime formatter for things that *really* have a time.
 * If backend sends a separate `service_date` + `time_slot`, you can
 * just use `time_slot` as text and `formatLocalDateString(service_date)`
 * for the date label.
 */
function formatDateTime(value) {
  if (!value) return '';
  const s = String(value);

  // If we only got a date, treat as local date
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return formatLocalDateString(s);
  }

  try {
    const dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return s;
    return dt.toLocaleString();
  } catch {
    return s;
  }
}

export default function Results() {
  const navigate = useNavigate();
  const q = useQuery();

  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState([]);
  const [error, setError] = useState('');

  const isOneWay = q.trip_type === 'oneway';
  const hasRequired =
    q.route_id &&
    q.passengers &&
    (isOneWay ? q.start_date : q.start_date && q.end_date);

  useEffect(() => {
    if (!hasRequired) {
      setError('Missing search parameters.');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError('');

        const params = new URLSearchParams({
          route_id: q.route_id,
          passengers: q.passengers,
          start_date: q.start_date,
        });

        if (!isOneWay && q.end_date) params.set('end_date', q.end_date);

        const url = `${API_BASE}/search?${params.toString()}`;

        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const list = Array.isArray(json)
          ? json
          : Array.isArray(json?.trips)
          ? json.trips
          : [];

        setTrips(list);
        if (list.length === 0)
          setError('No trips found for that search.');
      } catch (e) {
        console.error('Search error:', e);
        setError('Failed to load results. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [
    q.route_id,
    q.passengers,
    q.start_date,
    q.end_date,
    isOneWay,
    hasRequired,
  ]);

  const handleNewSearch = () => navigate('/');

  return (
    <div className="container mx-auto px-4 py-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Search results</h1>
        <button
          onClick={handleNewSearch}
          className="rounded-full bg-white text-black px-4 py-2 font-semibold hover:bg-white/90"
        >
          New search
        </button>
      </div>

      <div className="text-sm text-white/80 mb-6">
        <div>
          <span className="font-semibold">Trip:</span>{' '}
          {isOneWay ? 'One-way' : 'Return'}
        </div>
        <div>
          <span className="font-semibold">Route ID:</span> {q.route_id}
        </div>
        <div>
          <span className="font-semibold">Passengers:</span>{' '}
          {q.passengers}
        </div>
        <div>
          <span className="font-semibold">Start:</span>{' '}
          {formatLocalDateString(q.start_date)}
        </div>
        {!isOneWay && (
          <div>
            <span className="font-semibold">End:</span>{' '}
            {formatLocalDateString(q.end_date)}
          </div>
        )}
      </div>

      {loading && (
        <div className="rounded-xl bg-black/60 p-6 border border-white/10">
          Loading trips…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl bg-red-900/30 p-4 border border-red-700/40">
          {error}
        </div>
      )}

      {!loading && !error && trips.length > 0 && (
        <ul className="space-y-4">
          {trips.map((t) => {
            // Try to grab a clear "service date" from the trip, if present.
            const serviceDate =
              t.service_date || t.date || t.departure_date;

            return (
              <li
                key={t.id || `${t.departure_time}-${t.vehicle_id || 'v'}`}
                className="rounded-2xl bg-black/60 p-4 border border-white/10"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold">
                      {t.route_name || t.name || 'Shuttle'}
                    </div>
                    <div className="text-white/80">
                      {t.origin} → {t.destination}
                    </div>

                    {/* Date label – uses safe local formatter, so no more 12/13 off-by-one */}
                    {serviceDate && (
                      <div className="text-white/70 text-sm">
                        Date:{' '}
                        {formatLocalDateString(serviceDate)}
                      </div>
                    )}

                    <div className="text-white/70 text-sm">
                      {t.departure_time && (
                        <>
                          Departs:{' '}
                          {formatDateTime(t.departure_time)}
                        </>
                      )}
                      {t.arrival_time && (
                        <>
                          {' '}
                          • Arrives:{' '}
                          {formatDateTime(t.arrival_time)}
                        </>
                      )}
                    </div>

                    {typeof t.seats_left === 'number' && (
                      <div className="text-white/70 text-sm">
                        Seats left: {t.seats_left}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-xl font-bold">
                        {typeof t.price === 'number'
                          ? `$${t.price.toFixed(2)}`
                          : t.price || '—'}
                      </div>
                      <div className="text-xs text-white/60">
                        per passenger
                      </div>
                    </div>

                    <button
                      className="rounded-full bg-blue-600 hover:bg-blue-700 px-5 py-2 font-semibold"
                      onClick={() => {
                        const base = '/checkout';
                        const carry = new URLSearchParams({
                          ...q,
                          trip_id: String(t.id || ''),
                        });
                        navigate(`${base}?${carry.toString()}`);
                      }}
                    >
                      Select
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
